import './node.css';

import { animated, useSpring } from '@react-spring/web';
import { createUseGesture, dragAction } from '@use-gesture/react';
import { Fragment, MouseEvent, useEffect, useRef, useState } from 'react';

import { usePrevious } from '../../hooks/use-previous';
import { PositionedTree } from '../../pages/mindmap/types';
import { useMindmap, X_GAP } from '../../pages/mindmap/use-mindmap';
import { NodeAttachements } from './node-attachement/node-attachements';
import { NodeButtons } from './node-buttons/node-buttons';
import { NodeDndPreview } from './node-dnd-preview';
import { NodePreview } from './node-preview/node-preview';
import { NodeText } from './node-types/node-text';

export const MIN_NODE_WIDTH = 50;
export const MIN_NODE_HEIGTH = 45;
const LINEBREAK_WIDTH = 300;

interface NodeProps {
  node: PositionedTree;
  parentDragging?: boolean;
  isGhost?: boolean;
}

export type TempSize =
  | {
      width: number;
      height?: number;
    }
  | undefined;

const useGesture = createUseGesture([dragAction]);

export const Node = ({ node, parentDragging = false, isGhost = false }: NodeProps) => {
  const {
    interactableMap: { interactableMapSpring },
    contextMenu: { hideContextMenu },
    canvas: { addLine, removeLine },
    selection: { toggleNodeSelection, isNodeSelected, isEditingNode, selectedNodeIds, isDrawingSelection },
    dragAndDrop: { dropPreviewNode, isAnyNodeDragging, setIsAnyNodeDragging, dropDraggingElements, isRootNodeDragging },
    manipulation: { setShadowNodeAttachementsWidth },
  } = useMindmap();
  const [isThisNodeDragging, setIsThisNodeDragging] = useState<boolean>(false);
  const ref = useRef(null);

  const [tempTextMinSize, setTempTextMinSize] = useState<TempSize>();
  const [style, api] = useSpring(() => ({
    x: 0,
    y: 0,
    config: { tension: 0, friction: 0, mass: 0 },
  }));

  const setTempTextMinNewSize = (width: number, height: number) => {
    if (node.attachements) {
      // make text width not smaller than the largest attachements width
      const largestAttachementWidth = Math.max(...node.attachements.map(({ width }) => width));
      width = Math.max(width, largestAttachementWidth);
    }

    setTempTextMinSize({ width: width < MIN_NODE_WIDTH ? MIN_NODE_WIDTH : width, height });
  };

  const unsetTempTextMinNewSize = () => setTempTextMinSize(undefined);

  if (!isGhost) {
    useEffect(() => {
      if (node.children) {
        node.children.forEach((child) => {
          addLine(child.id, node.x + node.width / 2, node.y + node.height, child.x + child.width / 2, child.y);
        });
      }

      return () => {
        if (node.children) {
          node.children.forEach((child) => {
            removeLine(child.id);
          });
        }
      };
    }, [node.children]);
  }

  const selectNode = (e: MouseEvent) => {
    toggleNodeSelection(node.id, { append: e.ctrlKey });
    e.stopPropagation();
  };

  const selected = isNodeSelected(node.id);
  const contentEditing = selected && isEditingNode;

  const showDropPreview = dropPreviewNode?.id == node.id;

  const shouldDrag = (event: PointerEvent) => {
    const isMouse = event.pointerType == 'mouse';
    const isRoot = node.pid === null;
    return isMouse && !isRoot && !contentEditing;
  };

  const prevSelected = usePrevious(selected);
  useEffect(() => {
    // unset text shadow attachements width when unselected
    if (prevSelected && !selected) setShadowNodeAttachementsWidth(undefined);
  }, [selected]);

  if (!isGhost) {
    // handle node dragging
    useGesture(
      {
        onDrag: ({ event, movement: [x, y], down, type }) => {
          // check if its actual dragging or just clicking
          if (shouldDrag(event as PointerEvent) && type === 'pointermove') {
            // don't remove any other selected nodes
            if (!selected) toggleNodeSelection(node.id, { append: true });
            const scaleFactor = interactableMapSpring?.[0].scale?.get() || 1;
            api.start({ x: x / scaleFactor, y: y / scaleFactor, immediate: down });
            if (!interactableMapSpring?.[0].isNodeDragging.get()) {
              setIsAnyNodeDragging(true);
            }
            interactableMapSpring?.[1].set({ isNodeDragging: true });
            if (!isThisNodeDragging) {
              setIsThisNodeDragging(true);
            }
          }
        },
        onDragEnd: ({ event }) => {
          if (shouldDrag(event as PointerEvent) && selected && interactableMapSpring?.[0].isNodeDragging.get()) {
            const resetPosition = dropDraggingElements();
            if (resetPosition) {
              // move node back to start position if dropping was not successful
              api.start({ x: 0, y: 0, immediate: true });
            }
            setIsAnyNodeDragging(false);
          }
          interactableMapSpring?.[1].set({ isNodeDragging: false });
          if (isThisNodeDragging) {
            setIsThisNodeDragging(false);
          }
        },
      },
      { target: ref },
    );
  }

  const height =
    tempTextMinSize && tempTextMinSize.height
      ? tempTextMinSize.height + node.previewMinHeight + node.attachementMinHeight
      : node.height;
  const width = tempTextMinSize
    ? Math.max(tempTextMinSize.width, node.previewMinWidth, node.attachementMinWidth)
    : node.width;

  const showNodeButtons =
    selected &&
    !isAnyNodeDragging &&
    !isRootNodeDragging &&
    !isGhost &&
    selectedNodeIds.length <= 1 &&
    !isDrawingSelection;

  const prevShowNodeButtons = usePrevious(showNodeButtons);

  // hide tool panel when node buttons get hidden
  useEffect(() => {
    if (prevShowNodeButtons == true && showNodeButtons == false) hideContextMenu();
  }, [showNodeButtons]);

  return (
    <Fragment>
      {isAnyNodeDragging &&
        !isRootNodeDragging &&
        showDropPreview &&
        dropPreviewNode.position &&
        !isGhost &&
        {
          left: <NodeDndPreview left={node.x - X_GAP * 2 + X_GAP / 2} top={node.y + node.height / 2 - 50 / 2} />,
          right: <NodeDndPreview left={node.x + node.width - X_GAP / 2} top={node.y + node.height / 2 - 50 / 2} />,
          below: <NodeDndPreview left={node.x + node.width / 2 - X_GAP} top={node.y + node.height - 10} />,
        }[dropPreviewNode.position]}
      <animated.div
        ref={ref}
        style={{
          ...style,
          touchAction: 'none',
          opacity: parentDragging ? 0.6 : 1,
          zIndex: selected ? 10 : 2,
          position: 'absolute',
        }}
      >
        {showNodeButtons && <NodeButtons node={{ ...node, width, height: height || 0 }} />}
        <div
          className="node"
          style={{
            position: 'absolute',
            height,
            width,
            left: node.x,
            top: node.y,

            // mask all child elements
            overflow: 'hidden',

            borderRadius: 16,
            touchAction: 'none',
            ...(selected && !isGhost
              ? {
                  outline: '0 none',
                  outlineOffset: 0,
                  boxShadow: '0 0 0 0.2rem #BFDBFE',
                  borderColor: '#3B82F6',
                }
              : {}),
          }}
        >
          {node.attachements?.find((x) => x.type == 'file' && x.isPreview) && <NodePreview node={node} />}
          {
            {
              text: (
                <NodeText
                  node={node}
                  selected={selected}
                  selectNode={selectNode}
                  lineBreakWidth={LINEBREAK_WIDTH}
                  setTempTextMinNewSize={setTempTextMinNewSize}
                  unsetTempTextMinNewSize={unsetTempTextMinNewSize}
                />
              ),
            }[node.type]
          }
          <NodeAttachements node={node} selected={selected} />
        </div>
      </animated.div>
      {/* Show a ghost node on the original position of this node when it's dragging. */}
      {isThisNodeDragging && !isGhost && (
        <div style={{ opacity: 0.6 }}>
          <Node node={node} parentDragging={false} isGhost={true} />
        </div>
      )}
      {node.children &&
        node.children.map((child) => (
          <Node key={child.id} node={child} parentDragging={parentDragging || (selected && isAnyNodeDragging)} />
        ))}
    </Fragment>
  );
};
